import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';

export const SLR_LOAN_STANDING_FEATURE_FLAG = 'slrLoanStandingVerification';

export type SlrLoanStandingStatus =
  | 'unknown'
  | 'pending_upload'
  | 'processing'
  | 'eligible'
  | 'ineligible'
  | 'try_again'
  | 'cannot_determine';

export type SlrLoanStandingAction =
  'upload_document' | 'upload_newer_document' | 'confirm_name_match' | 'contact_support' | 'none';

/**
 * Raw good-standing state emitted by tio-api's EmployeePlanAccount
 * (`good_standing` column). The richer presentation status above
 * (`SlrLoanStandingStatus`) is derived from this plus the reason via
 * `deriveSlrLoanStandingStatus()`.
 */
export type SlrGoodStandingState = 'unknown' | 'eligible' | 'ineligible' | 'undeterminable';

/**
 * Raw good-standing reason emitted by tio-api's EmployeePlanAccount
 * (`good_standing_reason` column).
 */
export type SlrGoodStandingReason =
  | 'none'
  | 'missing_document'
  | 'stale_document'
  | 'outside_audit_window'
  | 'missing_required_observations'
  | 'name_mismatch'
  | 'status_not_in_good_standing'
  | 'delinquent_or_collections'
  | 'defaulted'
  | 'parent_plus_only'
  | 'undeterminable'
  | 'manual_override';

export interface SlrLoanStandingFailure {
  code: string;
  field?: string;
  message?: string;
}

export interface SlrLoanStandingPeriodRange {
  start?: string | null;
  end?: string | null;
  openAt?: string | null;
  closeAt?: string | null;
}

export interface SlrLoanStandingAuditState {
  employeePlanAccountId?: string | null;
  status: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action: SlrLoanStandingAction;
  auditWindow?: SlrLoanStandingPeriodRange | null;
  contributionPeriod?: SlrLoanStandingPeriodRange | null;
  sourceDocumentId?: string | null;
  featureEnabled: boolean;
}

export interface SlrLoanStandingCablePayload {
  employeePlanAccountId?: string | null;
  assetUuid?: string | null;
  sourceDocumentId?: string | null;
  status?: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action?: SlrLoanStandingAction;
}

export interface SlrLoanStandingDocumentUploadPayload {
  asset: DigitalAssetDetailSignature;
}

export interface SlrLoanStandingPresentation {
  titleKey: string;
  descriptionKey: string;
  ctaKey?: string;
  intent: 'info' | 'warning' | 'success';
  requiresAttention: boolean;
}
